import { createRoute, Link } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Badge } from "../components/ui/Badge/Badge";
import { Modal } from "../components/ui/Modal/Modal";
import { FormField } from "../components/ui/FormField/FormField";
import { Input } from "../components/ui/Input/Input";
import { Select } from "../components/ui/Select/Select";
import { ConfirmDialog } from "../components/ui/ConfirmDialog/ConfirmDialog";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { useContractDetail, useUpdateContract, useDeleteContract } from "../hooks/use-contracts";
import { BackButton } from "../components/ui/BackButton/BackButton";
import { formatDate } from "../lib/date";
import styles from "./ContractDetail.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/contracts/$id",
	component: withPermission(ContractDetailPage, "contracts", "read"),
});

function ContractDetailPage() {
	const { id } = route.useParams();
	const { t } = useTranslation();
	const contractQuery = useContractDetail(id);
	const updateContract = useUpdateContract();
	const deleteContract = useDeleteContract();
	const [editOpen, setEditOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [editData, setEditData] = useState<Record<string, string>>({});
	const [editError, setEditError] = useState("");

	if (contractQuery.isLoading) return <LoadingSpinner />;
	if (contractQuery.isError || !contractQuery.data)
		return <ErrorFallback onRetry={contractQuery.refetch} />;

	const contract = contractQuery.data;

	const openEdit = () => {
		setEditData({
			documentNumber: contract.documentNumber ?? "",
			title: contract.title,
			startDate: contract.startDate ? contract.startDate.slice(0, 10) : "",
			endDate: contract.endDate ? contract.endDate.slice(0, 10) : "",
			value: contract.value ?? "",
			currency: contract.currency ?? "",
			renewalNoticeDays: contract.renewalNoticeDays ? String(contract.renewalNoticeDays) : "",
			terms: contract.terms ?? "",
			notes: contract.notes ?? "",
			signedAt: contract.signedAt ? contract.signedAt.slice(0, 10) : "",
			signedBy: contract.signedBy ?? "",
			status: contract.status,
		});
		setEditOpen(true);
	};

	const saveEdit = () => {
		setEditError("");
		if (!editData.title?.trim()) {
			setEditError(t("fillRequiredFields"));
			return;
		}
		const payload: Record<string, unknown> = {};
		if (editData.documentNumber) payload.documentNumber = editData.documentNumber;
		if (editData.title) payload.title = editData.title;
		if (editData.startDate) payload.startDate = editData.startDate;
		if (editData.endDate) payload.endDate = editData.endDate;
		if (editData.value) payload.value = Number(editData.value);
		if (editData.currency) payload.currency = editData.currency;
		if (editData.renewalNoticeDays) payload.renewalNoticeDays = Number(editData.renewalNoticeDays);
		if (editData.terms) payload.terms = editData.terms;
		if (editData.notes) payload.notes = editData.notes;
		if (editData.signedAt) payload.signedAt = editData.signedAt;
		if (editData.signedBy) payload.signedBy = editData.signedBy;
		if (editData.status) payload.status = editData.status;

		updateContract.mutate(
			{ id, data: payload },
			{
				onSuccess: () => {
					setEditOpen(false);
					alert(t("updateSuccess"));
				},
				onError: (err) => setEditError(err instanceof Error ? err.message : t("updateFailed")),
			},
		);
	};

	return (
		<div>
			<BackButton fallbackTo="/contracts" />
			<PageHeader
				title={contract.documentNumber || `${t("contract")} #${contract.id.slice(0, 8)}`}
				action={
					<div className={styles.actions}>
						<Button variant="secondary" onClick={openEdit}>
							{t("edit")}
						</Button>
						<Button variant="danger" onClick={() => setDeleteOpen(true)}>
							{t("delete")}
						</Button>
					</div>
				}
			/>

			<div className={styles.grid}>
				<Card header={t("generalInfo")}>
					<div className={styles.infoGrid}>
						<Info label={t("title")} value={contract.title} />
						<Info label={t("type")} value={t(contract.type)} />
						<Info
							label={t("startDate")}
							value={contract.startDate ? formatDate(contract.startDate) : "-"}
						/>
						<Info
							label={t("endDate")}
							value={contract.endDate ? formatDate(contract.endDate) : "-"}
						/>
						<Info
							label={t("value")}
							value={`${contract.value || "-"} ${contract.currency || ""}`}
						/>
						<Info label={t("currency")} value={contract.currency} />
						<div>
							<span className={styles.infoLabel}>{t("status")}</span>
							<Badge
								variant={
									contract.status === "active"
										? "success"
										: contract.status === "expired"
											? "warning"
											: contract.status === "terminated"
												? "danger"
												: contract.status === "renewed"
													? "primary"
													: "default"
								}
							>
								{t(contract.status)}
							</Badge>
						</div>
						<Info label={t("autoRenewal")} value={contract.autoRenewal ? t("yes") : t("no")} />
						<Info
							label={t("renewalNoticeDays")}
							value={contract.renewalNoticeDays ? String(contract.renewalNoticeDays) : "-"}
						/>
						<Info
							label={t("signedAt")}
							value={contract.signedAt ? formatDate(contract.signedAt) : "-"}
						/>
						<Info label={t("signedBy")} value={contract.signedBy} />
						{contract.companyId && (
							<div>
								<span className={styles.infoLabel}>{t("company")}</span>
								<Link to={`/companies/${contract.companyId}` as never} className={styles.link}>
									{t("viewDetails")}
								</Link>
							</div>
						)}
						{contract.orderId && (
							<div>
								<span className={styles.infoLabel}>{t("order")}</span>
								<Link to={`/orders/${contract.orderId}` as never} className={styles.link}>
									{t("viewDetails")}
								</Link>
							</div>
						)}
					</div>
				</Card>

				<Card header={t("terms")}>
					<p className={styles.infoValue}>{contract.terms || "-"}</p>
				</Card>

				<Card header={t("notes")}>
					<p className={styles.infoValue}>{contract.notes || "-"}</p>
				</Card>
			</div>

			<Modal isOpen={editOpen} onClose={() => setEditOpen(false)} title={t("edit")}>
				{editError && <div className={styles.formError}>{editError}</div>}
				<FormField label={t("documentNumber")}>
					<Input
						value={editData.documentNumber ?? ""}
						onChange={(e) => setEditData({ ...editData, documentNumber: e.target.value })}
					/>
				</FormField>
				<FormField label={t("title")}>
					<Input
						value={editData.title ?? ""}
						onChange={(e) => setEditData({ ...editData, title: e.target.value })}
					/>
				</FormField>
				<FormField label={t("startDate")}>
					<Input
						type="date"
						value={editData.startDate ?? ""}
						onChange={(e) => setEditData({ ...editData, startDate: e.target.value })}
					/>
				</FormField>
				<FormField label={t("endDate")}>
					<Input
						type="date"
						value={editData.endDate ?? ""}
						onChange={(e) => setEditData({ ...editData, endDate: e.target.value })}
					/>
				</FormField>
				<FormField label={t("value")}>
					<Input
						type="number"
						value={editData.value ?? ""}
						onChange={(e) => setEditData({ ...editData, value: e.target.value })}
					/>
				</FormField>
				<FormField label={t("currency")}>
					<Input
						value={editData.currency ?? ""}
						onChange={(e) => setEditData({ ...editData, currency: e.target.value })}
					/>
				</FormField>
				<FormField label={t("renewalNoticeDays")}>
					<Input
						type="number"
						value={editData.renewalNoticeDays ?? ""}
						onChange={(e) => setEditData({ ...editData, renewalNoticeDays: e.target.value })}
					/>
				</FormField>
				<FormField label={t("terms")}>
					<Input
						value={editData.terms ?? ""}
						onChange={(e) => setEditData({ ...editData, terms: e.target.value })}
					/>
				</FormField>
				<FormField label={t("notes")}>
					<Input
						value={editData.notes ?? ""}
						onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
					/>
				</FormField>
				<FormField label={t("signedAt")}>
					<Input
						type="date"
						value={editData.signedAt ?? ""}
						onChange={(e) => setEditData({ ...editData, signedAt: e.target.value })}
					/>
				</FormField>
				<FormField label={t("signedBy")}>
					<Input
						value={editData.signedBy ?? ""}
						onChange={(e) => setEditData({ ...editData, signedBy: e.target.value })}
					/>
				</FormField>
				<FormField label={t("status")}>
					<Select
						options={[
							{ value: "draft", label: t("draft") },
							{ value: "active", label: t("active") },
							{ value: "expired", label: t("expired") },
							{ value: "terminated", label: t("terminated") },
							{ value: "renewed", label: t("renewed") },
						]}
						value={editData.status ?? ""}
						onChange={(e) => setEditData({ ...editData, status: e.target.value })}
					/>
				</FormField>
				<div className={styles.modalActions}>
					<Button variant="ghost" onClick={() => setEditOpen(false)}>
						{t("cancel")}
					</Button>
					<Button onClick={saveEdit} isLoading={updateContract.isPending}>
						{t("save")}
					</Button>
				</div>
			</Modal>

			<ConfirmDialog
				isOpen={deleteOpen}
				onClose={() => setDeleteOpen(false)}
				onConfirm={() =>
					deleteContract.mutate(id, {
						onSuccess: () => {
							setDeleteOpen(false);
							alert(t("deleteSuccess"));
						},
					})
				}
				message={t("confirmDelete")}
				isLoading={deleteContract.isPending}
			/>
		</div>
	);
}

function Info({ label, value }: { label: string; value: string | null }) {
	return (
		<div>
			<span className={styles.infoLabel}>{label}</span>
			<p className={styles.infoValue}>{value || "-"}</p>
		</div>
	);
}
